import { constant, noop } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { useImmer } from 'use-immer';

import { SkillDiscoveryItem as Item } from '@gmm/sdk-voodoo/skillDiscovery';
import { isSkillType, useMountedRef } from '@gmm/ui';
import { createNamedContext } from '~/lib/createNamedContext';
import { getIdentifier, isSkillIdentifier } from '~/work/skillDiscovery/utils';

export interface UseTreeExpansion {
  isExpanded: (item: Item | null) => boolean;
  resetExpansionMap: () => void;
  toggleExpansion: (item: Item) => void;
}

export const useTreeExpansion = (): UseTreeExpansion => {
  const mountedRef = useMountedRef();
  const [expansionMap, setExpansionMap] = useImmer<Record<string, boolean>>({});

  const resetExpansionMap = useCallback((): void => {
    /* istanbul ignore if */
    if (!mountedRef.current) return;

    setExpansionMap({});
  }, [mountedRef, setExpansionMap]);

  const toggleExpansion = useCallback(
    (item: Item): void => {
      /* istanbul ignore if */
      if (!mountedRef.current) return;

      const toggleId = getIdentifier(item);

      setExpansionMap(draft => {
        if (isSkillType(item)) {
          Object.keys(draft).forEach(id => {
            if (id !== toggleId && isSkillIdentifier(id)) {
              draft[id] = false;
            }
          });
        }

        draft[toggleId] = !draft[toggleId];
      });
    },
    [mountedRef, setExpansionMap],
  );

  return useMemo(() => {
    const isExpanded = (item: Item | null): boolean =>
      item ? !!expansionMap[getIdentifier(item)] : false;

    return {
      isExpanded,
      resetExpansionMap,
      toggleExpansion,
    };
  }, [expansionMap, resetExpansionMap, toggleExpansion]);
};

const TreeExpansionContext = createNamedContext<UseTreeExpansion>(
  'TreeExpansion',
  {
    isExpanded: constant(false),
    resetExpansionMap: noop,
    toggleExpansion: noop,
  },
);

export const TreeExpansionProvider = TreeExpansionContext.Provider;

export const useTreeExpansionContext = (): UseTreeExpansion =>
  useContext(TreeExpansionContext);
